import { useMutation, useQuery } from '@apollo/client';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Image } from 'expo-image';
import { useEffect, useLayoutEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import Animated, { SlideInRight, SlideOutLeft } from 'react-native-reanimated';
import { useDebounce } from 'use-debounce';
import { z } from 'zod';

import { Button } from '@oui/app-core/src/components/Button';
import { registerChatArtifactPreviewComponent } from '@oui/app-core/src/components/ChatArtifactPreview';
import { HeaderButtons } from '@oui/app-core/src/components/HeaderButtons';
import { Lead, Text, ThemeText } from '@oui/app-core/src/components/Text';
import { View } from '@oui/app-core/src/components/View';
import { RadioFormInput, useZodForm } from '@oui/app-core/src/form';
import { useArtifactRequest } from '@oui/app-core/src/hooks/useArtifactResult';
import { useTheme } from '@oui/app-core/src/styles';
import { graphql } from '@oui/lib/src/graphql/tada';
import {
  getStressSensitivityResult,
  StressSensitivityAssessment as StressSensitivityAssessmentSchema,
} from '@oui/lib/src/types/avro/stressSensitivityAssessment';
import { CompositionTemplates } from '@oui/lib/src/types/compositionTemplates';

import ResultBackground from './ResultBackground.png';
import { RootStackScreenProps } from '../../types/navigation';

export const StressSensitivityAssessmentQuery = graphql(`
  query StressSensitivityAssessment {
    user {
      ID
      role {
        ID
        composition(template: "STRESS_SENSITIVITY_ASSESSMENT") {
          ID
          json
        }
      }
    }
  }
`);

export const StressSensitivityAssessmentMutation = graphql(`
  mutation StressSensitivityAssessment($json: Map!, $roleID: UUID!) {
    setComposition(json: $json, roleID: $roleID, template: STRESS_SENSITIVITY_ASSESSMENT) {
      ID
      json
    }
  }
`);

const AssessmentSchema =
  StressSensitivityAssessmentSchema._def.innerType.shape.after._def.innerType;
const ORDER = [
  'feelingShortOfBreath',
  'racingHeart',
  'feelingDizzy',
  'troubleConcentrating',
] as const;
const LABELS: Record<keyof z.output<typeof AssessmentSchema>, string> = {
  feelingShortOfBreath: 'Feeling short of breath',
  feelingDizzy: 'Feeling dizzy',
  racingHeart: 'Racing heart',
  troubleConcentrating: 'Trouble concentrating',
};

const ITEMS = [
  {
    label: '1 - No Anxiety',
    value: 1,
  },
  {
    label: '2 - Little Anxiety',
    value: 2,
  },
  {
    label: '3 - Moderate Anxiety',
    value: 3,
  },
  {
    label: '4 - High Anxiety',
    value: 4,
  },
  {
    label: '5 - Extreme Anxiety',
    value: 5,
  },
];

/**
 * Gives user a stress sensitivity assessment and displays results.
 */
export const StressSensitivityAssessment = ({}: {}) => {
  const { theme } = useTheme();
  const { setOptions } =
    useNavigation<RootStackScreenProps<'StressSensitivityAssessment'>['navigation']>();
  const { params } = useRoute<RootStackScreenProps<'StressSensitivityAssessment'>['route']>();
  const { data: queryData, loading } = useQuery(StressSensitivityAssessmentQuery);
  const [update] = useMutation(StressSensitivityAssessmentMutation);

  const data = useMemo(() => {
    return loading
      ? undefined
      : CompositionTemplates.STRESS_SENSITIVITY_ASSESSMENT.parse(
          queryData?.user?.role?.composition?.json,
        );
  }, [queryData, loading]);

  const form = useZodForm(AssessmentSchema, {
    defaultValues: data?.STRESS_SENSITIVITY_ASSESSMENT[params.type] ?? {},
  });

  const formData = useWatch({ control: form.control }) as z.output<typeof AssessmentSchema>;

  const [currentStepIndex] = useDebounce(
    ORDER.findIndex((key) => typeof formData[key] !== 'number'),
    200,
  );
  const currentStepKey = ORDER[currentStepIndex];

  const isComplete = !currentStepKey;
  const { navigateToConversation } = useArtifactRequest('StressSensitivityAssessment', isComplete);

  useEffect(() => {
    if (!currentStepKey && queryData?.user?.role?.ID) {
      void update({
        variables: {
          roleID: queryData.user.role.ID,
          json: CompositionTemplates.STRESS_SENSITIVITY_ASSESSMENT.parse({
            STRESS_SENSITIVITY_ASSESSMENT: {
              ...data?.STRESS_SENSITIVITY_ASSESSMENT,
              [params.type]: formData,
            },
          }),
        },
      });
    }
    // We only need latest values of effect dependencies when currentStepKey has changed
    // eslint-disable-next-line
  }, [currentStepKey]);

  useLayoutEffect(() => {
    setOptions({
      headerTitle: 'Stress sensitivity assessment',
      headerRight: () => (
        <HeaderButtons>
          <Text
            text={!currentStepKey ? 'Result' : `${currentStepIndex + 1} of ${ORDER.length}`}
            size={15}
          />
        </HeaderButtons>
      ),
    });
  }, [setOptions, currentStepIndex, currentStepKey]);

  if (loading) return null;

  if (!currentStepKey) {
    const result = getStressSensitivityResult(formData);
    return (
      <View testID="StressSensitivityAssessment">
        <Image
          source={ResultBackground}
          style={{ aspectRatio: 375 / 261, position: 'absolute', top: 0, left: 0, right: 0 }}
        />
        <View
          style={{
            marginHorizontal: 20,
            marginTop: '30%',
            gap: 50,
          }}
        >
          <View
            style={{
              backgroundColor: 'white',
              borderRadius: 10,
              paddingVertical: 20,
              paddingHorizontal: 44,
              borderWidth: 1,
              borderBottomWidth: 3,
              borderColor: theme.color.dark,
            }}
          >
            <Lead text={result.title} textAlign="center" />
          </View>
          <Text text={result.description} textAlign="center" />
          <Button
            text="Done"
            variant="contained"
            alignSelf="center"
            onPress={navigateToConversation}
          />
        </View>
      </View>
    );
  }

  return (
    <View testID="StressSensitivityAssessment">
      <View
        style={{
          height: 5,
          backgroundColor: theme.color.accent300,
        }}
      >
        <View
          style={{
            width: `${((currentStepIndex + 1) / ORDER.length) * 100}%`,
            height: 5,
            backgroundColor: theme.color.accent100,
          }}
        />
      </View>
      <Text
        text="Please answer the following questions on a scale of 1-5."
        size={15}
        style={{ paddingTop: 10, marginHorizontal: 40, marginBottom: 80 }}
        textAlign="center"
      />
      <Animated.View
        key={currentStepKey}
        entering={SlideInRight.duration(300)}
        exiting={SlideOutLeft.duration(300)}
      >
        <RadioFormInput
          control={form.control}
          name={currentStepKey}
          label={() => (
            <View style={{ gap: 10, marginBottom: 20 }}>
              <ThemeText
                fontTheme={theme.typography.lead}
                weight="normal"
                text="Rate how much anxiety, fear, or distress is caused by"
                textAlign="center"
              />
              <Lead text={LABELS[currentStepKey]} textAlign="center" />
            </View>
          )}
          aria-label={`Rate how much anxiety, fear, or distress is caused by ${LABELS[currentStepKey]}`}
          items={ITEMS}
          labelWeight="semibold"
          labelSize="large"
        />
      </Animated.View>
    </View>
  );
};

registerChatArtifactPreviewComponent(
  'StressSensitivityAssessment',
  () => StressSensitivityAssessment,
);
